// Paso 6 (PE-B1): PDF del borrador (con marca de agua, no se guarda), "Aprobar versión" (solo admin; datos mínimos,
// incumplimientos con su acción y las 2 firmas en pantalla) e historial de versiones aprobadas (no se editan ni
// se borran; se descargan con URL firmada de 120 s). En el celular el PDF siempre se descarga.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignaturePad, { SignaturePadHandle } from '../../components/permits/SignaturePad';
import { logAudit } from '../../auditLog';
import { supabase } from '../../supabaseClient';
import { blobADataUrl } from '../../lib/permisos-evidencia';
import type { FilaPlan } from '../../lib/pe/pe-datos';
import type { EntradasPaso3 } from '../../lib/pe/pe-paso3';
import { ETIQUETA_ROL_FIRMA, fechaEc, generarPlanPDF, nombreArchivoPlan, RolFirmaPlan } from '../../lib/pe/pe-plan-pdf';
import {
  aprobarVersion, cargarVersiones, faltantesAprobacion, FilaVersion, ROLES_FIRMA, siguienteVersion, urlDescargaVersion, versionVigente,
} from '../../lib/pe/pe-versiones';
import type { DatosVista } from '../../lib/pe/pe-vista-previa';

export interface PropsPaso6Versiones {
  datos: DatosVista;
  plan: FilaPlan;
  entradas: EntradasPaso3;
  companyId: string;
  usuarioId: string | null;
  usuarioNombre?: string | null;         // nombre de quien está en la sesión (para el historial recién aprobado)
  puedeAprobar: boolean;                // solo admin (o superadmin entrando como la empresa)
  trabajadores: { id: string; full_name: string }[];
  onAprobada: () => Promise<void>;
  // PE-C1: cómo se muestra cada faltante (los requisitos no cambian; solo el nombre de la pantalla: capítulos en vez de pasos).
  textoFaltante?: (s: string) => string;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

function descargarUrl(url: string) {
  const a = document.createElement('a'); a.href = url; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
}

export default function Paso6Versiones(p: PropsPaso6Versiones) {
  const [versiones, setVersiones] = useState<FilaVersion[]>([]);
  // PE-A4b: el nombre de quien está en la sesión se conoce sin consultar; los demás se suman (nunca se reemplazan),
  // y la lista se muestra junto con los nombres para no pasar por "usuario sin nombre" justo después de aprobar.
  const [nombres, setNombres] = useState<Record<string, string>>(() => (p.usuarioId && p.usuarioNombre ? { [p.usuarioId]: p.usuarioNombre } : {}));
  const [error, setError] = useState(''); const [ok, setOk] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [firmantes, setFirmantes] = useState<Record<RolFirmaPlan, { nombre: string; cargo: string; conTrazo: boolean }>>({
    elabora: { nombre: '', cargo: '', conTrazo: false }, representante_legal: { nombre: '', cargo: '', conTrazo: false },
  });
  const pads = { elabora: useRef<SignaturePadHandle>(null), representante_legal: useRef<SignaturePadHandle>(null) };

  const recargar = useCallback(async () => {
    try {
      const v = await cargarVersiones(p.plan.id);
      const ids = Array.from(new Set(v.map(x => x.approved_by).filter((x): x is string => !!x && !(x === p.usuarioId && p.usuarioNombre))));
      let leidos: Record<string, string> = {};
      if (ids.length) {
        const { data, error: e } = await supabase.from('profiles').select('id, full_name, email').in('id', ids);
        if (e) console.error('Nombres de quienes aprobaron', e);
        leidos = Object.fromEntries((data ?? []).map((x: { id: string; full_name: string | null; email: string | null }) => [x.id, x.full_name || x.email || '']));
      }
      setNombres(n => ({ ...n, ...Object.fromEntries(Object.entries(leidos).filter(([, nombre]) => nombre)) }));
      setVersiones(v);
    } catch (e) { setError(msg(e)); }
  }, [p.plan.id, p.usuarioId, p.usuarioNombre]);
  useEffect(() => { recargar(); }, [recargar]);

  const numero = siguienteVersion(p.plan, versiones);
  const faltan = faltantesAprobacion({
    plan: p.plan, sectores: p.datos.sectores, evaluaciones: p.datos.evaluaciones, acciones: p.datos.acciones,
    firmas: ROLES_FIRMA.map(rol => ({ rol, ...firmantes[rol] })),
  });

  function descargarBorrador() {
    setError('');
    try {
      generarPlanPDF({ vista: p.datos, version: numero, estado: 'borrador', fecha: new Date(), firmas: [], trabajadores: p.trabajadores, marcaDeAgua: true })
        .save(nombreArchivoPlan(p.plan.code, numero, true));
    } catch (e) { setError(`No se pudo generar el borrador: ${msg(e)}`); }
  }

  async function aprobar() {
    if (faltan.length) return;
    setOcupado(true); setError('');
    try {
      const ahora = new Date();
      const firmas = [];
      for (const rol of ROLES_FIRMA) {
        const png = await pads[rol].current?.toBlob();
        if (!png) throw new Error('Falta una firma.');
        firmas.push({ rol, nombre: firmantes[rol].nombre, cargo: firmantes[rol].cargo, png, firmadoEn: ahora.toISOString(), imagen: await blobADataUrl(png) });
      }
      const pdf = generarPlanPDF({
        vista: p.datos, version: numero, estado: 'aprobada', fecha: ahora, trabajadores: p.trabajadores, marcaDeAgua: false,
        firmas: firmas.map(f => ({ rol: f.rol, nombre: f.nombre.trim(), cargo: f.cargo.trim() || null, firmadoEn: f.firmadoEn, imagen: f.imagen })),
      }).output('blob');
      const fila = await aprobarVersion({
        companyId: p.companyId, plan: p.plan, version: numero, pdf, usuarioId: p.usuarioId, anterior: versionVigente(versiones),
        firmas: firmas.map(({ imagen, ...f }) => f),
        snapshot: {
          empresa: p.datos.empresa, plan: p.plan, sectores: p.datos.sectores, entradas: p.entradas, evaluaciones: p.datos.evaluaciones,
          acciones: p.datos.acciones, brigadaMiembros: p.datos.brigadaMiembros,
        },
      });
      logAudit({ company_id: p.companyId, action: 'created', entity_type: 'emergency_plan', entity_id: fila.id,
        entity_name: `${p.plan.code ?? 'Plan de emergencias'} · versión ${numero}`, details: { evento: 'version_aprobada', version: numero, plan_id: p.plan.id } });
      setAbierto(false); setOk(`Versión ${numero} aprobada.`);
      await recargar(); await p.onAprobada();
    } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  async function descargar(v: FilaVersion) {
    setError('');
    try { if (v.pdf_path) descargarUrl(await urlDescargaVersion(v.pdf_path, nombreArchivoPlan(v.code, v.version, false))); }
    catch (e) { setError(msg(e)); }
  }

  useEffect(() => {
    if (!abierto) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !ocupado) setAbierto(false); };
    window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc);
  }, [abierto, ocupado]);

  return (
    <section className="pe2-card" aria-label="PDF y versiones">
      <h3>PDF y versiones</h3>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <div className="pe2-ok">{ok}</div>}
      <div className="pe2-acciones">
        <button type="button" className="pe2-btn" onClick={descargarBorrador}>Descargar borrador</button>
        {p.puedeAprobar && <button type="button" className="pe2-btn primario" onClick={() => { setOk(''); setAbierto(true); }}>Aprobar versión {numero}</button>}
      </div>
      <p className="pe2-nota" style={{ margin: 0 }}>El borrador lleva la marca de agua "BORRADOR" y no se guarda. Lo "por validar" no impide aprobar: queda en el anexo de pendientes.</p>
      <h4>Historial de versiones aprobadas</h4>
      {versiones.length === 0 ? <p className="pe2-nota">Aún no hay versiones aprobadas.</p> : (
        <ul className="pe2-vista-lista" aria-label="Versiones aprobadas">
          {versiones.map(v => (
            <li key={v.id} className="pe2-fila">
              <span className="ancho">
                <b>Versión {v.version}</b> · {v.approved_at ? fechaEc(new Date(v.approved_at)) : '—'} · aprobó {v.approved_by ? nombres[v.approved_by] || 'usuario sin nombre' : '—'}
                {' · '}{v.status === 'aprobada' ? 'vigente' : 'reemplazada'}
              </span>
              <button type="button" className="pe2-btn" disabled={!v.pdf_path} onClick={() => descargar(v)}>Descargar</button>
            </li>
          ))}
        </ul>
      )}

      {abierto && (
        <div className="pe2-modal-fondo">
          <div className="pe2-modal" role="dialog" aria-modal="true" aria-labelledby="pe2-aprobar-titulo">
            <h3 id="pe2-aprobar-titulo">Aprobar la versión {numero}</h3>
            <p className="pe2-nota" style={{ margin: 0 }}>Al aprobar se guardan una foto del plan y el PDF sin marca de agua; la versión no se puede cambiar ni borrar.</p>
            {ROLES_FIRMA.map(rol => (
              <div key={rol} className="pe2-card pe2-sub-card">
                <h4>{ETIQUETA_ROL_FIRMA[rol]}</h4>
                <label className="pe2-campo"><span>Nombre</span>
                  <input type="text" value={firmantes[rol].nombre} disabled={ocupado} onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], nombre: e.target.value } })} />
                </label>
                <label className="pe2-campo"><span>Cargo</span>
                  <input type="text" value={firmantes[rol].cargo} disabled={ocupado} onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], cargo: e.target.value } })} />
                </label>
                {/* El componente trae su propio botón "Borrar firma" (PE-B2b: había otro repetido). */}
                <SignaturePad ref={pads[rol]} disabled={ocupado} onChange={vacio => setFirmantes(f => ({ ...f, [rol]: { ...f[rol], conTrazo: !vacio } }))} />
              </div>
            ))}
            {faltan.length > 0 && (
              <div className="pe2-aviso" role="status">
                Para aprobar falta:
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{faltan.map((f, i) => <li key={i}>{p.textoFaltante ? p.textoFaltante(f) : f}</li>)}</ul>
              </div>
            )}
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => setAbierto(false)}>Cancelar</button>
              <button type="button" className="pe2-btn primario" disabled={ocupado || faltan.length > 0} onClick={aprobar}>{ocupado ? 'Aprobando…' : `Aprobar versión ${numero}`}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
